import { Component, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Location } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule } from '@ngx-translate/core';

import { DatacenterViewCustomizationComponent }
	from '../../datacenter/view-customization/datacenter-view-customization.component';
import { DatacenterDefaultConfiguration } from '../../datacenter/datacenter-default-configuration';
import { DatacenterFilterValidationService } from '../../services/datacenter-filter-validation.service';
import { DatacenterFiltersConfiguration } from '../../datacenter/datacenter-filters-configuration';
import { UserService } from '../../services/user.service';
import { DatacenterFilterUtilsService } from '../../services/datacenter-filter-utils.service';
import { TranslationService } from '../../services/translation.service';
import { State } from '../../../order/models/localization-state';
import { pageTransitions } from '../../animations/page-transitions';
import { CustomizedViewSetting } from '../../models/settings';
import { DatacenterType, UserDatacenterView } from '../../models/datacenter-aggrid-view';

import { ProductListViewService } from './services/product-list-view.service';
import { ProductListFilterCustomizationComponent }
	from './product-list-filter-customization.component';
import { ProductListSaveLayoutComponent }
	from './save-layout/product-list-save-layout.component';
import { ProductListSortingComponent }
	from './save-layout/product-list-sorting.component';

@Component({
	selector: 'copo-product-list-view-customization',
	templateUrl: './product-list-customization.component.html',
	styleUrl: './product-list-customization.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [

		MatButtonModule,
		MatIconModule,
		TranslateModule,

		MatTabsModule,
		ProductListFilterCustomizationComponent,
		ProductListSaveLayoutComponent,
		ProductListSortingComponent
	]
})
export class ProductListViewCustomizationComponent extends DatacenterViewCustomizationComponent {
	state: State;
	private readonly productListView: {
		currentView: UserDatacenterView;
		viewType: DatacenterType;
		viewSubType: string;
		viewList: UserDatacenterView[];
	};

	constructor(
		readonly _router: Router,
		readonly _route: ActivatedRoute,

		readonly _dialog: MatDialog,
		readonly _viewService: ProductListViewService,
		readonly _translationService: TranslationService,
		readonly _orderDefaultConf: DatacenterDefaultConfiguration,
		readonly _filterValidationService: DatacenterFilterValidationService,
		readonly _defaultFilterService: DatacenterFiltersConfiguration,
		readonly _filterService: DatacenterFilterUtilsService,
		readonly _userService: UserService,
		readonly _location: Location,
		readonly _snackBar: MatSnackBar
	) {
		super(
			_router,
			_route,
			_dialog,
			_viewService,
			_translationService,
			_orderDefaultConf,
			_filterValidationService,
			_defaultFilterService,
			_filterService,
			_userService,
			_snackBar
		);
		this.state = <State> _location.getState();
		this.filterLabel = this._translationService.getTR('datacenter.filters');
		this.layoutLabel = this._translationService.getTR('datacenter.layout-sorting');
		this.headerName = this.state.headerName;
		const { viewToEdit } = this._viewService;
		if (!viewToEdit) {
			throw new Error('bad configuration');
		}
		this.productListView = viewToEdit;
	}

	override async close(newView?: UserDatacenterView) {
		await this._router.navigate(['../'], {
			relativeTo: this._route,
			state: { ...this.getPreviousState(), view: newView, views: this.getViewList(newView) }
		});
	}

	getAdditionalInput() {
		return this.state.additionalInput;
	}

	override async back() {
		await this._router.navigate(
			['../'],
			{ relativeTo: this._route, state: { ...this.getPreviousState(), views: this.getViewList(undefined) } }
		);
	}

	override async setCurrentView() {
		this.isLoading = true;
		const readOnlyUser = this._userService.user.readOnly;
		const additionalInput = this.getAdditionalInput();
		const columnState = this.layoutView ? this.buildColumnState(this.layoutView) : [];
		const newView: UserDatacenterView = {
			id: super.getView().currentView.id,
			name: super.getView().currentView.name,
			isFavorite: Boolean(super.getView().currentView.isFavorite),
			type: this.datacenterType,
			columnState,
			sortModel: this.sortingView ? this.sortingView.getSortModel() : undefined,
			filterState: this.getAppliedFilters(),
			subType: this.getSubType()
		};
		if (!readOnlyUser && additionalInput?.['subCategory'] && additionalInput['form']) {
			const layoutKey = `EORDR_${ additionalInput['subCategory'] }_${ additionalInput['form'] }_layout`;
			const settingsType = this._userService.user.getSettings(layoutKey);
			let updated = false;
			if (settingsType) {
				try {
					const viewSetting = <CustomizedViewSetting> settingsType;
					if (JSON.stringify(viewSetting.value.columnState) !== JSON.stringify(columnState)
						|| JSON.stringify(viewSetting.value.sortModel)
						!== JSON.stringify(this.sortingView?.getSortModel())) {
						viewSetting.value.columnState = newView.columnState;
						viewSetting.value.sortModel = newView.sortModel;
						updated = true;
					}
				} catch (err) {
					console.error(err);
				}
			} else {
				updated = true;
				this._userService.user.settings.push({
					key: layoutKey,
					value: {
						columnState: newView.columnState,
						sortModel: newView.sortModel,
					}
				});
			}
			try {
				if (updated) {
					await this._userService.updateSettings();
				}
			} catch (error) {
				console.error('error while updating the settings', error);
			}
		}
		await this.close(newView);
	}

	private getSubType() {
		const additionalInput = this.getAdditionalInput();
		if (this.datacenterType === 'product-list' && additionalInput?.['subCategory']
			&& additionalInput['form']
		) {
			return `EORDR_${ additionalInput['subCategory'] }_${ additionalInput['form'] }_layout`;
		}
		return undefined;
	}

	private getViewList(newView: UserDatacenterView | undefined) {
		if (this.datacenterType === 'product-list') {
			if (newView) {
				const index = this.productListView.viewList.findIndex(i => newView.subType === i.subType);
				if (index !== -1) {
					this.productListView.viewList[index] = newView;
				} else {
					this.productListView.viewList.push(newView);
				}
			}
			return this.productListView.viewList;
		}
		return [];
	}
}
